import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, Output, ViewContainerRef, ViewEncapsulation } from '@angular/core';

import { DataDictionaryApi } from '../../../api/data-dictionary-api';
import { util } from '../../../classes/util';
import { InputConverter } from '../../../inputConverter';
import { FilterService } from '../../../services/filter.service';
import { LayerService } from '../../../services/layer.service';
import { ToSqlStringService } from '../../../services/to-sql-string.service';
import { IChildRecord, ILayer, ILayerColumn, IMapcoreMapImageLayer, IOperatorOption, IQuery, isQueryableMapCoreLayer, ITable, ITableColumn, IWorkingQuery } from '../../../types';
import { BaseComponent } from '../../base.component';

const shortWait = 10;

/** Child Records Filter Component */
@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-child-records-filter-ng',
    styleUrls: ['../../sharedStyles/base.scss', './child-records-filter.component.scss', '../../sharedStyles/telerik.scss'],
    templateUrl: './child-records-filter.component.html'
})
export class ChildRecordsFilterComponent extends BaseComponent implements OnDestroy {
    @Input() esriLayers: NonNullable<IMapcoreMapImageLayer[]>;
    _layerId: number;
    workingSql: string;
    tableColumns: ITableColumn[] = [];
    tableColumnsDisplay: any[] = [];
    operatorOptions: IOperatorOption[];
    disableSelectMenu: boolean;
    disableObj = false;

    get layerId() { return this._layerId; }
    @Input() set layerId(layerId: number) {
        this.setLayer(layerId);
    }
    @Input()
    esriLayerId: string;
    _query: IQuery;
    get query() {
        return this._query;
    }
    @Input() set query(inputQuery: IQuery) {
        this._query = inputQuery;
        this.execQuery();
    }

    _layerRef: ILayer;
    @Input() get layerRef() {
        return this._layerRef;
    }
    set layerRef(value: ILayer) {
        this._layerRef = value;
    }

    table: ITable;
    layerQuery: IQuery;
    layerColumns: ILayerColumn[] = [];
    selectedTable: ITable;
    _summaryItemsString = '';
    @Input() set summaryItemsString(inputSummary: string) {
        this._summaryItemsString = inputSummary;
    }
    get summaryItemsString() {
        return this._summaryItemsString;
    }

    showSQL: boolean;
    @Input() mapView: NonNullable<__esri.MapView>;
    @Input() selectedChildRecord: any = null;
    _workingQuery: IWorkingQuery = this.filterService.getEmptyQuery();
    @Input() set workingQuery(inputQuery: IWorkingQuery) {
        this._workingQuery = inputQuery;
    }
    get workingQuery() {
        return this._workingQuery;
    }

    @Input()
    @InputConverter()
    updateLayerOnApply: boolean = true;

    @Input() finalizeQuery = () => {
        this.query = this.filterService.getFinalizedQuery(this.workingQuery);
    }
    @Input() async updateWorkingSql(summaryString) {
        this.summaryItemsString = summaryString;
        await this.populateWorkingSql();
        this.changeDetectorRef.detectChanges();
    }
    @Input() updateQuery = (query) => {
        this._outsideUpdate = true;
        this._query = query;
    }
    private _outsideUpdate = false;
    @Output() public readonly workingQueryChange = new EventEmitter<{ workingQuery: IWorkingQuery; childColumns: ITableColumn[] }>();
    @Output() public readonly ondone = new EventEmitter<{ layerId: number; tableId: number; sql: string; query?: IQuery; childQuery?: IQuery }>();
    childRecords: IChildRecord[];
    @Output() public readonly onapply = new EventEmitter<{ id?: string; esriLayer?: any; data?: any }>();

    constructor(
        _vcr: ViewContainerRef,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly layerService: LayerService,
        private readonly filterService: FilterService,
        public toSqlStringService: ToSqlStringService,
        private readonly dataDictionaryApi: DataDictionaryApi) {
        super(_vcr);
        this.registerEvent('ondone', this.ondone);
        this.registerEvent('onapply', this.onapply);
        this.registerEvent('workingQueryChange', this.workingQueryChange);
        this.setOperatorOptions();
    }

    @Input()
    public activate = async () => {
        this.reset();
        await this.populateChildRecords();
        this.changeDetectorRef.detectChanges();
    }

    get andOr(): string {
        return this.workingQuery.childTableQuery.operator === 'OR' ? 'or' : 'and';
    }

    get showFilter() {
        return this.layerColumns.length > 0;
    }

    ngOnDestroy() {
        this.reset();
    }

    reset() {
        this.selectedChildRecord = null;
        this.childRecords = [];
    }

    async execQuery() {
        if (this.populateLayerDataPromise) {
            await this.populateLayerDataPromise;
        }
        this.refreshSubqueries();
    }

    populateLayerDataPromise: Promise<any>;

    async populateChildRecords() {
        this.childRecords = await this.layerService.getLayerChildTableRelationships(this.layerId);
        if (this.query && this.query.childTableRecord && this.query.childTableRecord.id && this.query.childTableRecord.id > 0) {
            this.selectedChildRecord = this.childRecords.filter(x => x.id === this._query.childTableRecord.id)[0];
            await this.populateSelectedChildRecord(this.selectedChildRecord.id);
        }
    }

    async setLayer(layerId: number) {
        this._layerId = layerId;
        this.workingSql = '';
        this.layerRef = undefined;
        this.table = null;
        this.layerColumns.length = 0;
        this.tableColumnsDisplay.length = 0;
        await this.populateLayerData();
        this.populateLayerDataPromise = this.populateChildRecords();
        await this.populateLayerDataPromise;
        this.populateWorkingQueryFromQuery();
        this.isChildRecordSelected();
        await this.refreshSubqueries();
        this.changeDetectorRef.detectChanges();
    }

    resetSubQueries() {
        this.workingQuery.childTableQuery.subqueries = [{ subqueries: [], operator: 'AND' }];
        this.filterService.clearValuesIfNeeded(this.workingQuery.childTableQuery.subqueries);
        this.workingQuery.childTableQuery.subqueries = this.filterService.populateWorkingSubqueryDisplayProperties(this.workingQuery.childTableQuery.subqueries, this.layerColumns);
    }
    remove(i: number) {
        this.workingQuery.childTableQuery.subqueries.splice(i, 1);
        this.refreshSubqueries();
    }

    setAll() {
        this.workingQuery.childTableQuery.operator = 'AND';
        this.refreshSubqueries();
    }

    setAny() {
        this.workingQuery.childTableQuery.operator = 'OR';
        this.refreshSubqueries();
    }

    setOperatorOptions() {
        this.operatorOptions = this.toSqlStringService.getOperatorOptions();
    }

    async childRecordChanged() {
        if (typeof this.selectedChildRecord === 'string') {
            this.selectedChildRecord = null;
        }

        if (this.selectedChildRecord) {
            const tableId = this.selectedChildRecord.id;
            await this.populateSelectedChildRecord(tableId);
        }
        this.workingQuery.childTableRecord = this.selectedChildRecord;
        this.resetSubQueries();
        //this.workingQuery.childTableQuery = {};
        await this.populateWorkingSql();
        this.isChildRecordSelected();
        this.changeDetectorRef.detectChanges();
        this.emitWorkingQuery();
    }
    async populateSelectedChildRecord(tableId: number) {
        this.selectedTable = await this.layerService.getTableColumns(tableId);
        this.tableColumns = this.selectedTable.columns;
        this.tableColumnsDisplay = this.tableColumns.sort((a, b) => (a.name > b.name) ? 1 : -1);
        if (this.selectedTable && this.selectedTable.metaTable && this.tableColumns) {
            const dataDictionaryColumns = await this.dataDictionaryApi.getColumnsWithDomainValuesByMetaTableId(this.selectedTable.metaTable.id);
            if (dataDictionaryColumns) {
                for (const column of this.tableColumns) {
                    const matchingColumn = dataDictionaryColumns.find(c => c.columnName.toLowerCase().trim() === column.name.toLowerCase().trim());
                    if (matchingColumn && matchingColumn.domainValues && matchingColumn.domainValues.length) {
                        column.domainValues = matchingColumn.domainValues;
                    }
                }
            }
        }
    }

    applyFilterChangeToLayer() {
        this.ondone.emit({ layerId: this.layerId, tableId: this.selectedTable.id, sql: this.workingSql, query: this.query});
    }
    addBottomSubqueryIfNeeded() {
        if (this.workingQuery.childTableQuery.subqueries) {
            let addWorkingSubquery: boolean = this.workingQuery.childTableQuery.subqueries.length === 0;
            if (!addWorkingSubquery) {
                const lastSubquery = this.workingQuery.childTableQuery.subqueries[this.workingQuery.childTableQuery.subqueries.length - 1];
                addWorkingSubquery = this.toSqlStringService.queryConditionIsValid(lastSubquery);
            }
            if (addWorkingSubquery) {
                this.workingQuery.childTableQuery.subqueries.push({ field: '', operator: '=', value: null});
            }
        }
    }

    async populateLayerData() {
        if (this.layerId) {
            const layers = await this.filterService.getLayerData(this.layerId);
            if (layers.length > 0) {
                this.layerRef = layers[0];
                const tables = this.layerRef.tables;
                this.table = tables.sort((a, b) => a.geometryType.localeCompare(b.geometryType))[0];
                if (this.layerRef.queryJSON) {
                    this.layerQuery = JSON.parse(this.layerRef.queryJSON);
                } else {
                    this.layerQuery = {};
                }
                this.layerColumns.length = 0;
                this.layerColumns.push(...layers[0].columns);
                for (const layerColumn of this.layerColumns) {
                    layerColumn.name = layerColumn.name.toLowerCase();
                }
                return this.layerColumns;
            }
        }
        return [];
    }

    populateWorkingQueryFromQuery() {
        if (this.query) {
            this.workingQuery = this.filterService.populateWorkingQueryFromQuery(this.query, this.tableColumns);
        }

    }
    async populateWorkingSql() {
        if (this.table) {
            this.workingSql = await this.filterService.populateWorkingSql(this.layerQuery, this.workingQuery, this.table, this.layerColumns);
        }
    }

    refreshSubqueries: () => void = util.debounce(async () => {
        await this.refreshSubqueriesWork();
    }, shortWait);

    private async refreshSubqueriesWork() {
        this.filterService.clearValuesIfNeeded(this.workingQuery.childTableQuery.subqueries);
        this.workingQuery.childTableQuery.subqueries = this.filterService.populateWorkingSubqueryDisplayProperties(this.workingQuery.childTableQuery.subqueries, this.layerColumns);
        await this.populateWorkingSql();
        if (this.selectedChildRecord && this.workingQuery.childTableQuery.subqueries && this.workingQuery.childTableQuery.subqueries.length) {
            const populatePromises: Promise<any>[] = [];
            this.workingQuery.childTableQuery.subqueries.forEach((sq, i) => {
                if (!sq.distinctValues) {
                    populatePromises.push(this.filterService.populateDistinctValues(this.selectedChildRecord.id, sq, (this.layerQuery && this.layerQuery.childTableQuery) ? this.layerQuery.childTableQuery : {}, this.workingQuery.childTableQuery, this.tableColumns, i, true, this.esriLayers && this.esriLayers.length ? this.esriLayers[0].mapcoreProps.isPETL : null));
                }
            });
            await Promise.all(populatePromises);
        }
        this.addBottomSubqueryIfNeeded();
        this.changeDetectorRef.detectChanges();
        this.showSelectMenu();
        this.emitWorkingQuery();
    }

    toggleShowSql() {
        this.showSQL = !this.showSQL;
    }

    emitWorkingQuery() {
        if (!this._outsideUpdate) {
            this.workingQueryChange.emit({ workingQuery: this.workingQuery, childColumns: this.tableColumns });
        }
        this._outsideUpdate = false;
        this.changeDetectorRef.detectChanges();
    }

    showSelectMenu() {
        let addWorkingSubquery = false;
        if (this.workingQuery && this.workingQuery.childTableQuery && this.workingQuery.childTableQuery.subqueries) {
            addWorkingSubquery = this.workingQuery.childTableQuery.subqueries.length === 0;
            if (!addWorkingSubquery) {
                const lastSubquery = this.workingQuery.childTableQuery.subqueries[0];
                addWorkingSubquery = this.toSqlStringService.queryConditionIsValid(lastSubquery);
            }
        }
        this.disableSelectMenu =  addWorkingSubquery;
    }

    layerData(layerRef) {
        const mcWindow = document.getElementById('mcWindow');
        let titleText = 'Filter';
        if (layerRef) {
            titleText = `Filter: ${layerRef.name}`;
        }
        mcWindow.title = titleText;
    }
    handleFilter(value, isColumnName, subquery?: IWorkingQuery) {
        if (isColumnName) {
            this.tableColumnsDisplay = this.tableColumns.filter((s) => util.LooseIncludes(s.name, value) || (s.alias && util.LooseIncludes(s.alias, value)));
        } else if (value.length > 1 && subquery.distinctValues) {
            subquery.filteredDistinctValues = subquery.distinctValues.filter(v => util.LooseIncludes(v.text, value) || util.LooseIncludes(v.value, value));
        } else {
            subquery.filteredDistinctValues = subquery.distinctValues;
        }
        this.changeDetectorRef.detectChanges();
    }

    valuechange() {
        this.changeDetectorRef.detectChanges();
        setTimeout(() => { this.refreshSubqueries(); }, shortWait);
    }

    async selectedFilterColumn(subquery: IWorkingQuery, subqueryIndex) {
        subquery.multiValue = null;
        subquery.value = null;
        this.changeDetectorRef.detectChanges();
        subquery = await this.filterService.populateDistinctValues(this.selectedChildRecord.id, subquery, this.layerQuery, this.workingQuery.childTableQuery, this.tableColumns, subqueryIndex, true, this.esriLayers && this.esriLayers.length ? this.esriLayers[0].mapcoreProps.isPETL : null);
        this.refreshSubqueries();
    }

    applyFilter() {
        this.finalizeQuery();
        const data = {
            layerId: this.layerId,
            tableId: this.selectedTable.id,
            sql: this.workingSql,
            query: this.query,
            childColumns: null
        };
        this.ondone.emit(data);

        if (this.updateLayerOnApply) {
            data.childColumns = this.tableColumns;
            this.onapply.emit({ id: data.layerId.toString(), data });

            const esriLayer = this.layerService.getEsriLayerByUniqueId(this.esriLayerId);
            if (esriLayer && data && isQueryableMapCoreLayer(esriLayer)) {
                esriLayer.mapcoreProps.filter = data.sql;
                esriLayer.mapcoreProps.query = data.query;
                esriLayer.mapcoreProps.childColumns = data.childColumns;
                this.layerService.updateLayerFilter(esriLayer, data.query);
                this.onapply.emit({ esriLayer });
            } else {
                this.onapply.emit(null);
            }

        }

    }

    cancel() {
        this.ondone.emit(null);
    }

    isChildRecordSelected() {
        this.disableObj =  (typeof this.selectedChildRecord === 'string') || this.selectedChildRecord === null || this.selectedChildRecord === undefined;
    }

    @Input()
    setFilter(layerId: number, esriLayerId: string, layerColumns: ILayerColumn[], query: IQuery) {
        this._layerId = layerId;
        this.esriLayerId = esriLayerId;
        this.layerColumns = layerColumns;
        this._query = query;
        this._outsideUpdate = true;
        this.setLayer(layerId);
    }
}
